import {
  BREAKFAST_MEAL_CONTAINER_ID,
  DINNER_CONTAINER_ID,
  LUNCH_CONTAINER_ID,
} from '@hungryroot/plan';
import {
  type PlanComponent,
  type PricingServicePricedContainer,
} from '@hungryroot/rest';

/**
 * Containers whose quantity represents a count of recipes that the customer
 * picks out. For those we render "{qty} recipe" / "{qty} recipes" so the
 * unit is explicit (matches how the post-checkout Plan page describes them).
 * Other RECIPE_QUANTITY containers (e.g. Snacks, Sweets) are item counts, not
 * recipes, so they render as a bare number.
 */
const RECIPE_CONTAINER_IDS = new Set<number>([
  BREAKFAST_MEAL_CONTAINER_ID,
  LUNCH_CONTAINER_ID,
  DINNER_CONTAINER_ID,
]);

export interface OccasionLineItem {
  containerId: number;
  /** Left-side label for the item, e.g. "Breakfast" or "Kids Snacks". */
  name: string;
  /**
   * Right-side value describing the occasion quantity, e.g. "2" for
   * RECIPE_QUANTITY containers or "2-3 servings" for TIERED containers.
   */
  quantity: string;
  sortOrder: number;
}

function getName(component: PlanComponent): string {
  return component.container_display_name?.trim() || component.container_name;
}

/**
 * Build the right-side quantity string for an occasion. Matches the
 * post-checkout Plan page conventions:
 *
 * - TIERED → "{min_servings}-{max_servings} servings" (e.g. "2-3 servings"),
 *   pulled from pricing service metadata because `container_quantity` on
 *   TIERED containers is a tier index, not a count. Falls back to the raw
 *   quantity when priced metadata isn't available.
 * - Lunch / Dinner → "{qty} recipe" / "{qty} recipes" so customers see the
 *   unit explicitly (these are the recipe-based occasions).
 * - Other RECIPE_QUANTITY containers → "{qty}" (e.g. "2").
 */
function getQuantityText(
  component: PlanComponent,
  pricedContainer: PricingServicePricedContainer | undefined
): string {
  if (component.container_pricing_strategy === 'TIERED') {
    if (pricedContainer?.TIERED) {
      const { min_servings, max_servings } = pricedContainer.TIERED;
      return `${min_servings}-${max_servings} servings`;
    }
    return '';
  }
  const quantity = component.container_quantity;
  if (RECIPE_CONTAINER_IDS.has(component.container_id)) {
    return `${quantity} ${quantity === 1 ? 'recipe' : 'recipes'}`;
  }
  return `${quantity}`;
}

/**
 * Build an ordered list of occasion line items for display. Components with
 * `container_quantity === 0` are omitted so empty occasions don't appear.
 * Items are sorted by `container_sort_order` so the list preserves the
 * customer's familiar occasion ordering.
 */
export function buildOccasionLineItems({
  components,
  pricedContainers = [],
}: {
  components: PlanComponent[];
  pricedContainers?: PricingServicePricedContainer[];
}): OccasionLineItem[] {
  const pricedById = new Map(pricedContainers.map((p) => [p.container_id, p]));

  return components
    .filter((component) => component.container_quantity > 0)
    .map((component) => ({
      containerId: component.container_id,
      name: getName(component),
      quantity: getQuantityText(
        component,
        pricedById.get(component.container_id)
      ),
      sortOrder: component.container_sort_order ?? Number.MAX_SAFE_INTEGER,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}
